import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {
  Card,
  DataStatus,
  type FormSubmitHandler,
  showNotification,
  type TabProps,
  TabsHeader,
} from 'platform/components';
import {Space, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  type CommonSubtitleAttribute,
  type CommonTitleExtensionAttribute,
  type PatchVehicleAdvertisingSettingsRequestBody,
  useDeleteVehicleLocalizedContentMutation,
  useEditGeneralSettingsMutation,
  useGetAuditsQuery,
  useGetDigitalCertificateThemesQuery,
  useGetGeneralSettingsQuery,
  useGetGenerationsQuery,
  useGetSalesPlatformsQuery,
  useGetVehicleLocalizedContentQuery,
  useGetVehicleQuery,
  useGetVersionsQuery,
  useSetVehicleLocalizedContentMutation,
} from '@dms/api/sales';
import {CONTENT_LANGUAGE_LABELS} from '@dms/api/settings';
import {type AdvertisingPlatformCodeEnum, type ApiError, EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  handleApiError,
  queryParams,
  useAdPlatformsVehicleUpdateDialog,
  useContentLanguages,
  usePermissions,
  useTenant,
  useVehiclePhotosPermissions,
} from '@dms/shared';

import {
  buildArray,
  getSafely,
  parseDate,
  type TestIdProps,
  useQueryState,
  useRequiredParams,
} from 'shared/web';

import {TABS_IDS} from '../constants/advertisementTabConstants';
import type {
  GeneralSettingsFormValues,
  LocalizedContentValues,
} from '../types/GeneralSettingsFormValues';
import {buildLocalizedContentDefaults} from '../utils/buildLocalizedContentDefaults';
import {getAttributeByCode} from '../utils/getAttributeByCode';
import {hasLocalizedContent} from '../utils/hasLocalizedContent';
import {isLocalizedEntryChanged} from '../utils/isLocalizedEntryChanged';
import {openAdSettingsEditDialog} from '../utils/openAdSettingsEditDialog';
import {resolveDefaultContentLanguage} from '../utils/resolveDefaultContentLanguage';
import {AdPlatforms} from './AdPlatforms';
import {GeneralSettingsForm} from './GeneralSettingsForm';
import {PublishedImages} from './PublishedImages';

function getDefaultLanguageValue(
  localizedContent: GeneralSettingsFormValues['localizedContent'],
  defaultLanguage: string | null,
  key: 'titleExtension' | 'subtitle'
): string | null {
  if (isNil(defaultLanguage)) {
    return null;
  }
  return getSafely(localizedContent, defaultLanguage)?.[key] ?? null;
}

interface AdvertisementDataProps extends TestIdProps {
  isPollingEnabled: boolean;
  canPreview?: boolean;
  onPreview?: (platformCode?: AdvertisingPlatformCodeEnum) => void;
  children?: React.ReactNode;
}

export const AdvertisementTab = (props: AdvertisementDataProps) => {
  const formatDateTime = useDateTimeFormatter();

  const {id: vehicleId} = useRequiredParams();
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.VEHICLE_ADVERTISEMENT_PLATFORMS);
  const [updateVehicleAds] = useAdPlatformsVehicleUpdateDialog();

  const {data: versionsData} = useGetVersionsQuery({vehicleId});
  const {data: generationsData} = useGetGenerationsQuery({vehicleId});
  const {data: auditsData} = useGetAuditsQuery({vehicleId});
  const {data: digitalCertificateThemesData} = useGetDigitalCertificateThemesQuery();

  const {data: vehicle} = useGetVehicleQuery({vehicleId});

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const [hasEditGeneralSettingsPermission] = usePermissions({
    permissionKeys: ['editGeneralSettings'],
    scopes: {
      editGeneralSettings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId,
  });

  const {isMultilanguageActive, enabledLanguages} = useContentLanguages();
  const {tenant} = useTenant();
  const defaultContentLanguage = resolveDefaultContentLanguage(
    tenant?.defaultLocale,
    enabledLanguages
  );
  // isLoading (not isFetching) — the form must not render before the first response, because the
  // default-language title is seeded from it and a submit made against unseeded values could
  // delete a language the user never saw. Refetches keep the form mounted.
  const {data: localizedContentData, isLoading: isLocalizedContentLoading} =
    useGetVehicleLocalizedContentQuery(isMultilanguageActive ? {vehicleId} : skipToken);
  const {data: platforms} = useGetSalesPlatformsQuery({vehicleId});

  const languageOptions = enabledLanguages.map((code) => ({
    value: code,
    label: i18n.t(CONTENT_LANGUAGE_LABELS[code]),
  }));

  const [setLocalizedContent] = useSetVehicleLocalizedContentMutation();
  const [deleteLocalizedContent] = useDeleteVehicleLocalizedContentMutation();

  const versionOptions = versionsData?.map((item) => ({value: item.code, label: item.name})) ?? [];
  const generationOptions =
    generationsData?.map((item) => ({value: item.code, label: item.name})) ?? [];
  const digitalCertificateOptions =
    auditsData?.map((item) => ({
      value: item.id,
      label: isNotNil(item.updatedAt)
        ? `${item.name} (${formatDateTime('dateShort', parseDate(item.updatedAt))})`
        : item.name,
    })) ?? [];
  const digitalCertificateThemeOptions =
    digitalCertificateThemesData?.map((item) => ({value: item.id, label: item.name})) ?? [];

  const {
    data: settings,
    isLoading,
    isError,
  } = useGetGeneralSettingsQuery({vehicleId}, {skip: isNilOrEmpty(vehicleId)});

  const {isLoading: isPlatformsLoading} = useGetSalesPlatformsQuery({vehicleId});

  const [editSettings] = useEditGeneralSettingsMutation();

  // Seed default-language content from general values and mirror it back on submit.
  const generalTitleExtension =
    getAttributeByCode<CommonTitleExtensionAttribute>(
      'title_extension',
      settings?.general?.attributes
    )?.value ?? null;
  const generalSubtitle =
    getAttributeByCode<CommonSubtitleAttribute>('subtitle', settings?.general?.attributes)?.value ??
    null;
  const initialLocalizedContent = buildLocalizedContentDefaults(
    enabledLanguages,
    localizedContentData,
    {
      language: defaultContentLanguage,
      titleExtension: generalTitleExtension,
      subtitle: generalSubtitle,
    }
  );

  const submitLocalizedContent = async (
    localizedContent: Record<string, LocalizedContentValues> | undefined
  ) => {
    if (isNil(localizedContent)) {
      return;
    }

    const promises = Object.entries(localizedContent)
      .filter(([language, entry]) =>
        isLocalizedEntryChanged(entry, getSafely(initialLocalizedContent, language))
      )
      .map(([language, entry]) => {
        if (hasLocalizedContent(entry)) {
          return setLocalizedContent({
            vehicleId,
            language,
            body: {
              titleExtension: entry.titleExtension,
              subtitle: entry.subtitle,
              description: entry.description,
            },
          }).unwrap();
        }
        return deleteLocalizedContent({vehicleId, language}).unwrap();
      });

    await Promise.all(promises);
  };

  const handleSubmit: FormSubmitHandler<GeneralSettingsFormValues> = async (
    values,
    _,
    reset,
    blockerContext
  ) => {
    const isPerPlatformAttributesEnabled = isFeatureEnabled(
      featureFlags.ADVERTISEMENT_PER_PLATFORM_ATTRIBUTES
    );
    const body: PatchVehicleAdvertisingSettingsRequestBody = {
      general: {
        attributes: [
          {
            code: 'description' as const,
            value: values.description,
            override: true,
            publish: true,
          },
          {
            code: 'title_extension',
            value: isMultilanguageActive
              ? getDefaultLanguageValue(
                  values.localizedContent,
                  defaultContentLanguage,
                  'titleExtension'
                )
              : values.title,
            override: true,
            publish: true,
          },
          {
            code: 'subtitle',
            value: isMultilanguageActive
              ? getDefaultLanguageValue(values.localizedContent, defaultContentLanguage, 'subtitle')
              : values.subtitle,
            override: true,
            publish: true,
          },
          {
            code: 'version',
            value: values.version,
            override: true,
            publish: true,
          },
          {
            code: 'generation',
            value: values.generation,
            override: true,
            publish: true,
          },
          {
            code: 'digital_certificate',
            value: values.digitalCertificate,
            override: true,
            publish: true,
          },
          {
            code: 'digital_certificate_theme',
            value: values.digitalCertificateTheme,
            override: true,
            publish: true,
          },
          {
            code: 'video_url',
            value: values.videoUrl,
            override: true,
            publish: true,
          },
          {
            code: 'condition',
            value: isNotNilOrEmpty(values.condition) ? Number(values.condition?.[0]) : null,
            override: true,
            publish: true,
          },
          ...(isPerPlatformAttributesEnabled
            ? [
                {
                  code: 'highlights' as const,
                  // T20-123039 US 2 AC #7 — sparse-array compaction: drop nulls/empties
                  // so payload becomes ["a", "b"] not ["a", null, "b", null].
                  value: compactHighlights([
                    values.highlight1,
                    values.highlight2,
                    values.highlight3,
                  ]),
                  override: true,
                  publish: true,
                },
                {
                  code: 'model_range' as const,
                  value: values.modelRange,
                  override: true,
                  publish: true,
                },
                {
                  code: 'trim_line' as const,
                  value: values.trimLine,
                  override: true,
                  publish: true,
                },
              ]
            : []),
        ],
      },
    };

    await editSettings({vehicleId, patchVehicleAdvertisingSettingsRequestBody: body})
      .unwrap()
      .then(() => {
        if (isMultilanguageActive) {
          return submitLocalizedContent(values.localizedContent);
        }
        return undefined;
      })
      .then(() => {
        showNotification.success(i18n.t('general.notifications.advertisement.dataEditSubtitle'));
        updateVehicleAds(vehicleId);
        reset(undefined, {keepValues: true});
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const handleEdit = () =>
    openAdSettingsEditDialog({
      vehicleId,
      isDisabled: !hasEditGeneralSettingsPermission,
    });

  const tabs = buildArray<TabProps>()
    .add({
      id: TABS_IDS.LISTING_ATTRIBUTES,
      title: i18n.t('entity.vehicle.labels.advertisementTabGeneralTitle'),
      'data-testid': testIds.vehicles.publishing('listingAttributes'),
      content: (
        <DataStatus
          isLoading={isLoading || isPlatformsLoading || isLocalizedContentLoading}
          isError={isError}
        >
          <GeneralSettingsForm
            data={settings?.general}
            vehicleId={vehicleId}
            descriptionLabel={i18n.t('page.advertisement.labels.platformsDescription')}
            versionOptions={versionOptions}
            generationOptions={generationOptions}
            digitalCertificateOptions={digitalCertificateOptions}
            digitalCertificateThemeOptions={digitalCertificateThemeOptions}
            onSubmit={handleSubmit}
            onHandleEdit={handleEdit}
            isDisabled={!hasEditGeneralSettingsPermission}
            isMultilanguageActive={isMultilanguageActive}
            enabledLanguages={languageOptions}
            platforms={platforms}
            initialLocalizedContent={initialLocalizedContent}
            data-testid={props['data-testid']}
          />
        </DataStatus>
      ),
    })
    .when(canReadVehicleAlbumPhotos, {
      id: TABS_IDS.PUBLISHED_IMAGES,
      title: i18n.t('entity.vehicle.labels.photos'),
      'data-testid': testIds.vehicles.publishing('publishedImages'),
      content: <PublishedImages />,
    });

  return (
    <VStack spacing={4}>
      <Card>
        <TabsHeader
          variant="condensed"
          onChange={setActiveTabId}
          activeTabId={activeTabId}
          tabs={tabs}
        />
        <Space vertical={4} />
        {(tabs.find((tab) => tab.id === activeTabId) ?? tabs[0])?.content}
      </Card>
      <AdPlatforms
        vehicleId={vehicleId}
        isPollingEnabled={props.isPollingEnabled}
        canPreview={props.canPreview}
        onPreview={props.onPreview}
      />
    </VStack>
  );
};

function compactHighlights(values: Array<string | null | undefined>): string[] | null {
  const compacted = values.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  return compacted.length === 0 ? null : compacted;
}
